/**
 * BI > Analítico Estoque — modal de filtros e exportação CSV / prévia JSON.
 * Base: doc/queries/relatorio-estoque-completo.sql
 */
import api from '../api.js';
import { translate } from '../../versa/filter.js';
import { notify } from '../../versa/msgbox.js';
import dialog from '../../versa/dialog.js';

function $(sel, root = document) {
	return root.querySelector(sel);
}

function skuLabel(p) {
	if (!p?.sku) return '';
	return Array.isArray(p.sku) ? p.sku.join('') : String(p.sku);
}

function filterExprCompany(companyId) {
	return translate({
		'field-0': 'company_id',
		'operator-0': 'eq',
		'value-0': companyId,
	});
}

async function loadCompanies(selectEl) {
	const res = await api.list_all_companies();
	if (!res.ok) {
		notify(await res.text(), 'Empresas');
		return;
	}
	let list;
	try {
		list = await res.json();
	} catch (e) {
		notify('Resposta de empresas inválida.', 'Empresas');
		return;
	}
	if (!Array.isArray(list)) return;
	selectEl.innerHTML = '<option value="">— Selecione —</option>';
	for (const c of list) {
		const o = document.createElement('option');
		o.value = c.id;
		o.textContent = c.nick || c.name || c.id;
		selectEl.appendChild(o);
	}
}

async function loadWarehouses(companyId, sel) {
	sel.innerHTML = '<option value="">Todos</option>';
	if (!companyId) return;
	const fe = encodeURIComponent(filterExprCompany(companyId));
	const res = await api.list_warehouses(`?filter=${fe}&limit=0&sort=2`);
	if (!res.ok) {
		notify(await res.text(), 'Armazéns');
		return;
	}
	const arr = await res.json();
	if (!Array.isArray(arr)) return;
	for (const w of arr) {
		const o = document.createElement('option');
		o.value = String(w.id);
		o.textContent = `${w.name ?? ''} (${w.id})`;
		sel.appendChild(o);
	}
}

async function loadPrograms(companyId, sel) {
	sel.innerHTML = '<option value="">Todos</option>';
	if (!companyId) return;
	const res = await api.list_programs_by_company(companyId);
	if (!res.ok) {
		notify(await res.text(), 'Programas');
		return;
	}
	const arr = await res.json();
	if (!Array.isArray(arr)) return;
	for (const p of arr) {
		const o = document.createElement('option');
		o.value = String(p.id);
		o.textContent = p.name || String(p.id);
		sel.appendChild(o);
	}
}

async function loadProductsForCompany(companyId, selectEl, searchInput) {
	selectEl.innerHTML = '';
	if (!companyId) {
		selectEl.innerHTML = '<option value="">— Escolha a empresa —</option>';
		return;
	}
	const fe = filterExprCompany(companyId);
	const batch = 500;
	const products = [];
	const seen = new Set();
	for (let offset = 0; ; offset += batch) {
		const q = `?filter=${encodeURIComponent(fe)}&limit=${batch}&offset=${offset}&sort=7&include_disabled=1`;
		const res = await api.list_products(q);
		if (!res.ok) {
			notify(await res.text(), 'Produtos');
			selectEl.innerHTML = '<option value="">— Erro —</option>';
			return;
		}
		const chunk = await res.json();
		if (!Array.isArray(chunk) || chunk.length === 0) break;
		for (const p of chunk) {
			const id = p?.id;
			if (id == null || seen.has(id)) continue;
			seen.add(id);
			products.push(p);
		}
		if (chunk.length < batch) break;
	}
	selectEl.innerHTML = '<option value="">— Todos (ou filtre SKU/nome) —</option>';
	for (const p of products) {
		const sku = skuLabel(p);
		const name = (p.name || '').trim() || '(sem nome)';
		const o = document.createElement('option');
		o.value = String(p.id);
		const dis = p.is_disabled ? ' [inativo]' : '';
		o.textContent = `${sku} — ${name} (id ${p.id})${dis}`;
		selectEl.appendChild(o);
	}
	if (searchInput) applyProductSearch(selectEl, searchInput.value);
}

function applyProductSearch(selectEl, needle) {
	const n = (needle || '').trim().toLowerCase();
	if (!n) {
		for (const o of selectEl.options) o.hidden = false;
		return;
	}
	for (const o of selectEl.options) {
		if (!o.value) {
			o.hidden = false;
			continue;
		}
		o.hidden = !String(o.textContent || '').toLowerCase().includes(n);
	}
}

function collectParams(node, format) {
	const companyId = $('#bisa-company', node)?.value?.trim();
	if (!companyId) return null;
	const sp = new URLSearchParams();
	sp.set('format', format);
	const wh = $('#bisa-warehouse', node)?.value?.trim();
	if (wh) sp.set('warehouse_id', wh);
	const prog = $('#bisa-program', node)?.value?.trim();
	if (prog) sp.set('program_id', prog);
	const prod = $('#bisa-product', node)?.value?.trim();
	if (prod) sp.set('product_id', prod);
	else {
		const ps = $('#bisa-product-search', node)?.value?.trim();
		if (ps) sp.set('product_search', ps);
	}
	const lot = $('#bisa-lot', node)?.value?.trim();
	if (lot) sp.set('lot_search', lot);
	if (format === 'json') {
		sp.set('limit', $('#bisa-limit', node)?.value || '200');
		sp.set('offset', $('#bisa-offset', node)?.value || '0');
	} else {
		const mx = $('#bisa-max-rows', node)?.value?.trim();
		if (mx) sp.set('max_rows', mx);
	}
	return sp;
}

function escCell(s) {
	if (s == null) return '';
	const t = document.createElement('template');
	t.textContent = String(s);
	return t.innerHTML;
}

function renderPreviewTable(container, rows) {
	if (!rows?.length) {
		container.innerHTML = '<p class="muted mts">Nenhuma linha (ajuste filtros ou permissões).</p>';
		return;
	}
	const keys = Object.keys(rows[0]);
	let html = '<div class="bisa-scroll mts"><table class="view fsx"><thead><tr>';
	for (const k of keys) {
		html += `<th>${escCell(k)}</th>`;
	}
	html += '</tr></thead><tbody>';
	for (const r of rows) {
		html += '<tr>';
		for (const k of keys) {
			html += `<td>${escCell(r[k])}</td>`;
		}
		html += '</tr>';
	}
	html += '</tbody></table></div>';
	container.innerHTML = html;
}

function downloadCsv(blob, filename) {
	const a = document.createElement('a');
	a.href = URL.createObjectURL(blob);
	a.download = filename || 'estoque-analitico.csv';
	a.click();
	URL.revokeObjectURL(a.href);
}

export default async function bi_stock_analytical() {
	const work = document.getElementById('workarea');
	work.innerHTML = `
		<style>
			.bisa-hint { color: #555; font-size: 0.9rem; max-width: 48rem; }
			.bisa-scroll { overflow: auto; max-height: 65vh; }
			.bisa-modal label { display: block; margin-top: 0.65rem; }
			.bisa-modal .row { display: flex; flex-wrap: wrap; gap: 0.75rem 1rem; align-items: flex-end; margin-top: 0.35rem; }
			.bisa-modal .row > label { flex: 1 1 14rem; }
			.bisa-product-select { width: 100%; min-height: 11rem; margin-top: 0.35rem; font-size: 0.88rem; }
			.bisa-actions { margin-top: 1rem; display: flex; flex-wrap: wrap; gap: 0.5rem; align-items: center; }
		</style>
		<div class="title">Analítico Estoque (BI)</div>
		<p class="mts bisa-hint">Abra o formulário para filtrar por empresa, programa, armazém, produto (lista por SKU/nome) e trecho do lote. Exporte <strong>CSV</strong> (Excel) ou gere uma <strong>prévia</strong> na tela. Critérios alinhados a <code>doc/queries/relatorio-estoque-completo.sql</code>.</p>
		<p class="mts"><button type="button" class="button" id="bisa-open-modal">Filtros e geração</button></p>
		<div id="bisa-preview-host" class="mts"></div>
	`;

	const previewHost = $('#bisa-preview-host', work);

	const openModal = () => {
		const dlgHtml = `
<div class="msgbox bisa-modal" style="min-width: min(40rem, 92vw); max-width: 96vw; text-align: left;">
	<div class="mb-title">Analítico Estoque — filtros</div>
	<div class="mb-text" style="max-height: none;">
		<label>Empresa<sup>*</sup><select id="bisa-company"></select></label>
		<div class="row">
			<label>Programa<select id="bisa-program"></select></label>
			<label>Armazém<select id="bisa-warehouse"></select></label>
		</div>
		<label>Filtrar lista de produtos (SKU ou nome)<input type="search" id="bisa-prod-filter" placeholder="opcional" autocomplete="off"></label>
		<label>Produto<select id="bisa-product" class="bisa-product-select" size="10"></select></label>
		<p class="muted fsxx" style="margin:0.25rem 0 0;">Deixe produto em «Todos» e use o campo abaixo para buscar por SKU/nome no servidor, ou escolha um item na lista.</p>
		<label>Busca produto (servidor, se não escolheu na lista)<input type="text" id="bisa-product-search" placeholder="trecho SKU ou nome" autocomplete="off"></label>
		<label>Lote (contém)<input type="text" id="bisa-lot" placeholder="opcional" autocomplete="off"></label>
		<div class="row">
			<label>Prévia: limite<input type="number" id="bisa-limit" value="200" min="1" max="5000" step="1"></label>
			<label>Prévia: offset<input type="number" id="bisa-offset" value="0" min="0" step="1"></label>
			<label>CSV: máx. linhas<input type="number" id="bisa-max-rows" value="50000" min="1" max="200000" step="1"></label>
		</div>
	</div>
	<div class="bisa-actions">
		<button type="button" class="button bn_Preview">Prévia na tela</button>
		<button type="button" class="button bn_ExportCsv">Exportar CSV</button>
		<button type="button" class="button bn_Escape">Fechar</button>
	</div>
</div>`;

		const dlg = dialog.create(dlgHtml, async (key) => {
			const root = dlg.getNode();
			if (key === 'Escape') {
				dlg.destroy();
				return;
			}
			if (key !== 'Preview' && key !== 'ExportCsv') return;

			const sp = collectParams(root, key === 'Preview' ? 'json' : 'csv');
			if (!sp) {
				notify('Selecione a empresa.');
				return;
			}
			const companyId = $('#bisa-company', root).value.trim();
			try {
				if (key === 'Preview') {
					const res = await api.stock_complete_report(companyId, sp.toString());
					const raw = await res.text();
					if (!res.ok) {
						notify(raw || res.statusText, 'Analítico Estoque');
						return;
					}
					let data;
					try {
						data = JSON.parse(raw);
					} catch (e) {
						notify('Resposta não é JSON.', 'Analítico Estoque');
						return;
					}
					if (!Array.isArray(data)) {
						notify('Formato inesperado da API.', 'Analítico Estoque');
						return;
					}
					dlg.destroy();
					renderPreviewTable(previewHost, data);
					notify(`Prévia: ${data.length} linha(s).`, 'Analítico Estoque');
					return;
				}
				const res = await api.stock_complete_report(companyId, sp.toString());
				if (!res.ok) {
					const t = await res.text();
					notify(t || res.statusText, 'Analítico Estoque');
					return;
				}
				const blob = await res.blob();
				const cd = res.headers.get('Content-Disposition');
				let fname = 'estoque-analitico.csv';
				if (cd) {
					const m = cd.match(/filename="?([^";]+)"?/i);
					if (m) fname = m[1];
				}
				downloadCsv(blob, fname);
				dlg.destroy();
				notify('Download do CSV iniciado.', 'Analítico Estoque');
			} catch (e) {
				console.error(e);
				notify(e?.message || String(e), 'Analítico Estoque');
			}
		});

		const root = dlg.getNode();
		const selCo = $('#bisa-company', root);
		const selWh = $('#bisa-warehouse', root);
		const selPr = $('#bisa-program', root);
		const selProd = $('#bisa-product', root);
		const inpFilt = $('#bisa-prod-filter', root);

		loadCompanies(selCo).then(() => {});
		selCo.addEventListener('change', async () => {
			const id = selCo.value;
			await Promise.all([
				loadWarehouses(id, selWh),
				loadPrograms(id, selPr),
				loadProductsForCompany(id, selProd, inpFilt),
			]);
		});
		inpFilt.addEventListener('input', () => applyProductSearch(selProd, inpFilt.value));
		selProd.addEventListener('change', () => applyProductSearch(selProd, inpFilt.value));
	};

	$('#bisa-open-modal', work).addEventListener('click', openModal);
}
